import React from "react";
import { makeStyles, InputBase } from "@material-ui/core";

interface TextAreaProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  error?: string;
  description?: string;
  disabled?: boolean;
  rows?: number;
}

const useStyles = makeStyles(() => ({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    width: "100%",
  },
  label: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#364153",
    marginBottom: "4px",
  },
  inputWrapper: {
    display: "flex",
    alignItems: "flex-start",
    padding: "8px",
    borderRadius: "8px",
    border: "1px solid #E2E8F0",
    backgroundColor: "white",
    transition: "all 0.2s ease",
    "&:focus-within": {
      borderColor: "#2B7FFF",
      boxShadow: "0 0 0 3px rgba(43, 127, 255, 0.1)",
    },
  },
  inputWrapperError: {
    borderColor: "#EF4444",
    "&:focus-within": {
      borderColor: "#EF4444",
      boxShadow: "0 0 0 3px rgba(239, 68, 68, 0.1)",
    },
  },
  inputWrapperDisabled: {
    backgroundColor: "#F8FAFC",
    cursor: "not-allowed",
  },
  input: {
    flex: 1,
    fontSize: "16px",
    color: "#334155",
    width: "100%",
    resize: "vertical" as const,
    "&::placeholder": {
      color: "#94A3B8",
      opacity: 1,
    },
  },
  helperText: {
    fontSize: "14px",
    marginTop: "4px",
  },
  errorText: {
    color: "#EF4444",
  },
  descriptionText: {
    color: "#64748B",
  },
}));

const TextArea = ({
  value,
  onChange,
  label,
  placeholder,
  error,
  description,
  disabled = false,
  rows = 4,
}: TextAreaProps) => {
  const classes = useStyles();

  const inputWrapperClasses = [
    classes.inputWrapper,
    error ? classes.inputWrapperError : "",
    disabled ? classes.inputWrapperDisabled : "",
  ]
    .filter(Boolean)
    .join(" ");

  const showHelper = !error && description;

  return (
    <div className={classes.container}>
      {label && <label className={classes.label}>{label}</label>}

      <div className={inputWrapperClasses}>
        <InputBase
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          className={classes.input}
          multiline
          rows={rows}
          fullWidth
        />
      </div>

      {error && <span className={`${classes.helperText} ${classes.errorText}`}>{error}</span>}

      {showHelper && (
        <span className={`${classes.helperText} ${classes.descriptionText}`}>{description}</span>
      )}
    </div>
  );
};

export default TextArea;
